import { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Loader from "../components/Loader";

function Home() {
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  // Bulk order form state
  const [bulkForm, setBulkForm] = useState({
    name: "",
    phone: "",
    vehicle: "",
    route: "",
    volume: "",
  });
  const [bulkNote, setBulkNote] = useState(
    "Your bulk enquiry will help us plan the right vehicle mix and schedule."
  );
  const [bulkSubmitted, setBulkSubmitted] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: "",
    mobile: "",
    subject: "",
    message: "",
  });
  const [contactNote, setContactNote] = useState(
    "We will only use your information to contact you about your request."
  );
  const [contactSubmitted, setContactSubmitted] = useState(false);

  // Year
  const currentYear = new Date().getFullYear();

  // Loader hide logic — mirrors the JS: hide after 'load' + 650ms, or hard cap 1800ms
  useEffect(() => {
    let loadTimer;
    let capTimer;

    const hideLoader = () => {
      clearTimeout(loadTimer);
      clearTimeout(capTimer);
      setLoading(false);
    };

    const onLoad = () => {
      loadTimer = setTimeout(hideLoader, 650);
    };

    if (document.readyState === "complete") {
      loadTimer = setTimeout(hideLoader, 650);
    } else {
      window.addEventListener("load", onLoad);
    }

    capTimer = setTimeout(hideLoader, 1800);

    return () => {
      window.removeEventListener("load", onLoad);
      clearTimeout(loadTimer);
      clearTimeout(capTimer);
    };
  }, []);

  // Sync body loading class
  useEffect(() => {
    if (loading) {
      document.body.classList.add("loading");
    } else {
      document.body.classList.remove("loading");
    }
  }, [loading]);

  // Nav link click closes menu
  const handleNavLinkClick = () => {
    setMenuOpen(false);
  };

  // Bulk order form handlers
  const handleBulkChange = (e) => {
    const { name, value } = e.target;
    setBulkForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBulkSubmit = (e) => {
    e.preventDefault();
    setBulkForm({ name: "", phone: "", vehicle: "", route: "", volume: "" });
    setBulkNote(
      "Thanks. Your bulk order request has been received and the Zarkit team will contact you soon."
    );
    setBulkSubmitted(true);
  };

  // Contact form handlers
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setContactForm({ name: "", mobile: "", subject: "", message: "" });
    setContactNote(
      "Thanks for contacting Zarkit. Our team will reach out shortly."
    );
    setContactSubmitted(true);
  };

  return (
    <>
      {loading && (
        <Loader />
      )}

      <div className="site-shell">
        {/* Header — passes menu state so Header can render toggle + nav */}
        <Header
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          onNavLinkClick={handleNavLinkClick}
        />

        <main>
          {/* ── Hero ── */}
          <section className="hero" id="home">
            <div className="container hero-layout">
              <div className="hero-copy">
                <p className="eyebrow">Now Serving Jhansi</p>
                <h1>Har Bar Time Pe Delivery.</h1>
                <p className="hero-text">
                  Zarkit is a local goods transport platform for Jhansi. From
                  market stock and shop dispatches to house shifting and urgent
                  deliveries, we connect customers with the right vehicle for
                  every load.
                </p>

                <div className="hero-actions">
                  <a
                    className="btn btn-primary"
                    href="https://play.google.com/store/apps/details?id=com.zarkit.zarkit_user"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download User App
                  </a>

                  <a
                    className="btn btn-secondary"
                    href="https://play.google.com/store/apps/details?id=com.zarkit.zarkit_partner"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download Partner App
                  </a>
                </div>

                <div className="hero-highlights">
                  <article>
                    <strong>Local coverage</strong>
                    <span>
                      Currently operating in Jhansi with a focused local
                      network.
                    </span>
                  </article>
                  <article>
                    <strong>Flexible fleet</strong>
                    <span>
                      2 wheeler to truck bookings for personal and business
                      transport.
                    </span>
                  </article>
                  <article>
                    <strong>Bulk support</strong>
                    <span>
                      Built for repeat bookings, stock movement and business
                      orders.
                    </span>
                  </article>
                </div>
              </div>

              <div className="hero-panel">
                <div className="hero-brand-card">
                  <img
                    src="/zarkit-group.png"
                    alt="Zarkit Group logo"
                    className="hero-brand-image"
                  />
                  <div className="hero-route">
                    <span>Pickup</span>
                    <div className="route-dots"></div>
                    <span>On-time drop</span>
                  </div>
                  <div className="hero-stats">
                    <div>
                      <small>City</small>
                      <strong>Jhansi</strong>
                    </div>
                    <div>
                      <small>Service</small>
                      <strong>Business + Personal</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Trust Strip ── */}
          <section className="trust-strip">
            <div className="container trust-grid">
              <div>
                <strong>For local businesses</strong>
                <span>
                  Inventory movement, shop-to-shop supply and recurring delivery
                  support.
                </span>
              </div>
              <div>
                <strong>For personal shifting</strong>
                <span>
                  Furniture, appliances, room shifting and everyday goods
                  movement.
                </span>
              </div>
              <div>
                <strong>For partner drivers</strong>
                <span>
                  Steady trip flow through a dedicated partner app experience.
                </span>
              </div>
            </div>
          </section>

          {/* ── About ── */}
          <section className="section about-section" id="about">
            <div className="container about-layout">
              <div className="section-heading compact-heading">
                <p className="eyebrow">About Zarkit</p>
                <h2>A transport brand designed around Jhansi.</h2>
              </div>
              <div className="about-copy">
                <p>
                  Zarkit is focused on one thing: making goods movement in
                  Jhansi simpler, faster and more dependable. Instead of a
                  one-size-fits-all approach, we offer vehicle options that
                  match the size of the job, whether it is a single parcel,
                  business stock refill or a full shifting load.
                </p>
                <p>
                  The platform is built for both sides of the network. Customers
                  get quick booking support, and partner drivers get a clear way
                  to receive and manage trips.
                </p>
              </div>
            </div>
          </section>

          {/* ── Apps ── */}
<section className="section app-section" id="apps">
  <div className="container">
    <div className="section-heading">
      <p className="eyebrow">Apps</p>
      <h2>Separate apps for customers and driver partners.</h2>
      <p>
        Keep each journey simple: booking for users, trip management
        for partners.
      </p>
    </div>

    <div className="app-grid">
      {/* USER APP */}
      <article className="app-card">
        <span className="app-badge">For Users</span>
        <h3>Customer Booking App</h3>
        <p>
          Book a vehicle, schedule pickups and manage personal or
          business transport from one clean flow.
        </p>

        <a
          href="https://play.google.com/store/apps/details?id=com.zarkit.zarkit_user"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/google-play-badge.png"
            alt="Get it on Google Play"
            style={{ width: "180px", marginTop: "10px" }}
          />
        </a>
      </article>

      {/* PARTNER APP */}
      <article className="app-card">
        <span className="app-badge">For Partners</span>
        <h3>Driver Partner App</h3>
        <p>
          Accept trips, view route details and manage work with a
          dedicated partner-side app.
        </p>

        <a
          href="https://play.google.com/store/apps/details?id=com.zarkit.zarkit_partner"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/google-play-badge.png"
            alt="Get it on Google Play"
            style={{ width: "180px", marginTop: "10px" }}
          />
        </a>
      </article>
    </div>
  </div>
</section>

          {/* ── Vehicle Fleet ── */}
          <section className="section vehicle-section" id="vehicles">
            <div className="container">
              <div className="section-heading">
                <p className="eyebrow">Vehicle Fleet</p>
                <h2>Vehicle options for every type of movement.</h2>
              </div>

              <div className="vehicle-grid">
                <article className="vehicle-card">
                  <h3>2 Wheeler</h3>
                  <p>For documents, parcels and small urgent deliveries.</p>
                </article>
                <article className="vehicle-card">
                  <h3>3 Wheeler</h3>
                  <p>For cartons, shop stock and compact shifting jobs.</p>
                </article>
                <article className="vehicle-card">
                  <h3>4 Wheeler</h3>
                  <p>For furniture, electronics and larger city-wide loads.</p>
                </article>
                <article className="vehicle-card">
                  <h3>Truck</h3>
                  <p>
                    For heavy goods, house shifting and bulk transport
                    requirements.
                  </p>
                </article>
                <article className="vehicle-card">
                  <h3>Bike Loader</h3>
                  <p>
                    For compact commercial loads and quick market-side movement.
                  </p>
                </article>
              </div>
            </div>
          </section>

          {/* ── Services / Use Cases ── */}
          <section className="section services-section" id="business">
            <div className="container">
              <div className="section-heading">
                <p className="eyebrow">Use Cases</p>
                <h2>
                  Structured for daily business movement and personal shifting.
                </h2>
              </div>

              <div className="service-grid">
                <article className="service-card">
                  <span className="service-index">01</span>
                  <h3>Business Deliveries</h3>
                  <p>
                    For market dispatches, supplier movement, retail stock and
                    repeat business deliveries.
                  </p>
                </article>
                <article className="service-card">
                  <span className="service-index">02</span>
                  <h3>Personal Shifting</h3>
                  <p>
                    For home items, furniture, appliances and local room or
                    office shifting within Jhansi.
                  </p>
                </article>
                <article className="service-card">
                  <span className="service-index">03</span>
                  <h3>Bulk Orders</h3>
                  <p>
                    For recurring transport, multiple loads and higher volume
                    movement with coordinated planning.
                  </p>
                </article>
                <article className="service-card">
                  <span className="service-index">04</span>
                  <h3>Partner Network</h3>
                  <p>
                    For driver partners looking to grow with a focused local
                    logistics platform.
                  </p>
                </article>
              </div>

              <div className="benefit-strip">
                <div>
                  <strong>Single-city focus</strong>
                  <span>Jhansi-first operations with local service attention.</span>
                </div>
                <div>
                  <strong>Fleet flexibility</strong>
                  <span>
                    The right vehicle based on load, route and urgency.
                  </span>
                </div>
                <div>
                  <strong>Operational clarity</strong>
                  <span>
                    Simple enquiry and app-based flow for customers and
                    partners.
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* ── Bulk Order Form ── */}
          <section className="section bulk-section" id="bulk-order">
            <div className="container bulk-layout">
              <div className="bulk-copy">
                <p className="eyebrow">Bulk Order</p>
                <h2>
                  Share your transport requirement and our team will plan the
                  movement.
                </h2>
                <p>
                  Ideal for shops, distributors, warehouses and businesses that
                  need repeat trips or multi-vehicle support within Jhansi.
                </p>

                <div className="bulk-points">
                  <div>
                    <strong>Multi-vehicle support</strong>
                    <span>
                      Plan multiple trips or vehicle combinations based on the
                      load.
                    </span>
                  </div>
                  <div>
                    <strong>Structured for repeat orders</strong>
                    <span>
                      Useful for regular dispatches, seasonal spikes and
                      recurring business movement.
                    </span>
                  </div>
                </div>
              </div>

              <form className="info-form" onSubmit={handleBulkSubmit}>
                <label>
                  Business or Person Name
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your name or company name"
                    value={bulkForm.name}
                    onChange={handleBulkChange}
                    required
                  />
                </label>
                <label>
                  Phone Number
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter Phone Number"
                    value={bulkForm.phone}
                    onChange={handleBulkChange}
                    required
                  />
                </label>
                <label>
                  Vehicle Needed
                  <select
                    name="vehicle"
                    value={bulkForm.vehicle}
                    onChange={handleBulkChange}
                    required
                  >
                    <option value="">Select vehicle type</option>
                    <option>2 Wheeler</option>
                    <option>3 Wheeler</option>
                    <option>4 Wheeler</option>
                    <option>Truck</option>
                    <option>Bike Loader</option>
                    <option>Multiple Vehicle Types</option>
                  </select>
                </label>
                <label>
                  Pickup and Drop Details
                  <textarea
                    name="route"
                    rows="4"
                    placeholder="Write pickup area, drop area and delivery timing"
                    value={bulkForm.route}
                    onChange={handleBulkChange}
                    required
                  />
                </label>
                <label>
                  Order Volume
                  <textarea
                    name="volume"
                    rows="4"
                    placeholder="Tell us number of trips, goods type, load size or frequency"
                    value={bulkForm.volume}
                    onChange={handleBulkChange}
                    required
                  />
                </label>
                <button className="btn btn-primary wide-btn" type="submit">
                  Submit Bulk Order Request
                </button>
                <p
                  className="form-note"
                  style={
                    bulkSubmitted
                      ? { color: "#111111", fontWeight: "700" }
                      : {}
                  }
                >
                  {bulkNote}
                </p>
              </form>
            </div>
          </section>

          {/* ── Jhansi City Section ── */}
          <section className="section city-section" id="jhansi">
            <div className="container">
              <div className="city-panel">
                <p className="eyebrow">Zarkit in Jhansi</p>
                <h2>
                  Focused local operations, built specifically for Jhansi.
                </h2>
                <p>
                  Zarkit currently serves Jhansi only. That focus helps us
                  shape a service that understands local movement patterns,
                  business needs and delivery expectations instead of spreading
                  too wide too early.
                </p>
                <div className="city-points">
                  <div>
                    <strong>Only in Jhansi right now</strong>
                    <span>
                      A focused launch market with local support and better
                      control over service quality.
                    </span>
                  </div>
                  <div>
                    <strong>Useful across the city</strong>
                    <span>
                      From market areas to home shifting routes, Zarkit is
                      designed for city-wide movement.
                    </span>
                  </div>
                  <div>
                    <strong>Growing with local partners</strong>
                    <span>
                      Customer demand and driver partner support are being built
                      together at the city level.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Contact ── */}
          <section className="section contact-section" id="contact">
            <div className="container contact-layout">
              <div className="contact-copy">
                <p className="eyebrow">Contact Us</p>
                <h2>Speak with the Zarkit team.</h2>
                <p>
                  For bookings, business enquiries, partner onboarding or
                  support, use the contact details below.
                </p>

                <div className="contact-cards">
  <a className="contact-tile" href="tel:+918882283046">
    <span className="tile-label" style={{ display: "block" }}>
      Call Us:
    </span>
    <strong>+91 88822 83046</strong>
  </a>

  <a
    className="contact-tile"
    href="mailto:support@zarkit.com"
  >
    <span className="tile-label" style={{ display: "block" }}>
      Email:
    </span>
    <strong>support@zarkit.com</strong>
  </a>

  <div className="contact-tile">
    <span className="tile-label" style={{ display: "block" }}>
      Office:
    </span>
    <strong>
      Jouri Bujurg, Near Parichha, Jhansi, Uttar Pradesh – 284121, India
    </strong>
  </div>
</div>
              </div>

              <form className="info-form" onSubmit={handleContactSubmit}>
                <label>
                  Full Name
                  <input
                    type="text"
                    name="name"
                    placeholder="Your full name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    required
                  />
                </label>
                <label>
                  Mobile Number
                  <input
                    type="tel"
                    name="mobile"
                    placeholder="Enter Phone Number"
                    value={contactForm.mobile}
                    onChange={handleContactChange}
                    required
                  />
                </label>
                <label>
                  Subject
                  <input
                    type="text"
                    name="subject"
                    placeholder="Business delivery, shifting, support or partnership"
                    value={contactForm.subject}
                    onChange={handleContactChange}
                    required
                  />
                </label>
                <label>
                  Message
                  <textarea
                    name="message"
                    rows="5"
                    placeholder="Tell us how Zarkit can help"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    required
                  />
                </label>
                <button className="btn btn-dark wide-btn" type="submit">
                  Send Enquiry
                </button>
                <p
                  className="form-note"
                  style={
                    contactSubmitted
                      ? { color: "#111111", fontWeight: "700" }
                      : {}
                  }
                >
                  {contactNote}
                </p>
              </form>
            </div>
          </section>
        </main>

        {/* Footer — passes currentYear so Footer can render it */}
        <Footer currentYear={currentYear} />
      </div>
    </>
  );
}

export default Home;
